import type { AgeBand, HomeworkUploadInput } from "@orbin/contracts";

/**
 * The homework photo judge: a vision classifier that reads a child's photo before anything keeps or
 * sends it. Only a verdict of exactly "allow" passes. A judge that fails, times out, answers outside
 * the schema, or has no vision route refuses the photo, so an unjudged photo is never a pass.
 */

export const HOMEWORK_IMAGE_RUBRIC_VERSION = "kids-homework-image-v1";

export const HOMEWORK_IMAGE_TIMEOUT_MS = 15000;

/**
 * The rubric the classifier applies. It is versioned and must be calibrated against reviewed examples
 * before the homework route is opened; until then the route refuses every photo.
 */
export const HOMEWORK_IMAGE_RUBRIC = [
  "You judge one photo that a child aged 6 to 17 took of their homework.",
  'Answer only with a JSON object: {"verdict": "allow" or "refuse", "categories": [strings]}.',
  "Allow only a photo of schoolwork: paper, a notebook, a book page, or an exercise on a screen.",
  "Refuse when the photo shows a person's face or body, nudity or sexual content, violence, self-harm, drugs,",
  "an identity document, a payment card, a home address or a screen of messages, or anything that is not schoolwork.",
  "When unsure, refuse.",
].join("\n");

export type HomeworkImageMime = HomeworkUploadInput["mimeType"];

export interface HomeworkImageJudgeInput {
  rubricVersion: typeof HOMEWORK_IMAGE_RUBRIC_VERSION;
  ageBand: AgeBand;
  mimeType: HomeworkImageMime;
  imageBase64: string;
}

/** The provider-neutral image judge. The caller supplies the adapter; this module names no provider. */
export interface HomeworkImageJudge {
  judge(input: HomeworkImageJudgeInput, signal: AbortSignal): Promise<unknown>;
}

export type HomeworkImageDecision = "allow" | "refuse";

/** Reads a judge answer. Only { verdict: "allow" } passes; every other shape is a refusal. */
export function parseHomeworkImageVerdict(answer: unknown): HomeworkImageDecision {
  if (typeof answer !== "object" || answer === null || Array.isArray(answer)) return "refuse";
  return (answer as { verdict?: unknown }).verdict === "allow" ? "allow" : "refuse";
}

/** Judges one photo. A judge error, a timeout or a bad answer is a refusal. */
export async function judgeHomeworkImage(
  judge: HomeworkImageJudge,
  input: Omit<HomeworkImageJudgeInput, "rubricVersion">,
): Promise<HomeworkImageDecision> {
  const signal = AbortSignal.timeout(HOMEWORK_IMAGE_TIMEOUT_MS);
  try {
    const answer = await judge.judge(
      { ...input, rubricVersion: HOMEWORK_IMAGE_RUBRIC_VERSION },
      signal,
    );
    return parseHomeworkImageVerdict(answer);
  } catch {
    return "refuse";
  }
}
